/** @format */

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Rating } from "react-simple-star-rating";
import { getProductDetail, outOfStock, getProductReview } from "../../store/product/product.action";
import { getDefaultCurrency } from "../../store/currency/currency.action";
import formatImageUrl, { formatAmount } from "../../extra/functions";
import defaultImage from "../../../assets/images/default.jpg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const saleTypeLabel = (type) => {
	switch (type) {
		case 1:
			return "Buy Now";
		case 2:
			return "Auction";
		case 3:
			return "Not for Sale";
		default:
			return "-";
	}
};

const ProductDetail = () => {
	const { productDetail, review } = useSelector((state) => state.product);
	const { defaultCurrency } = useSelector((state) => state.currency);
	const location = useLocation();
	const { state } = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [currentPage, setCurrentPage] = useState(1);
	const [size] = useState(10);
	const [activeImgIndex, setActiveImgIndex] = useState(0);
	const [lightboxImg, setLightboxImg] = useState(null);

	const productId = typeof state === "string" ? state : state?._id;
	const product = typeof state === "object" && state !== null ? state : productDetail;
	const symbol = defaultCurrency?.symbol || "";

	useEffect(() => {
		if (typeof state === "string" && state) {
			dispatch(getProductDetail(state));
		}
		dispatch(getDefaultCurrency());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productId]);

	useEffect(() => {
		if (productId) {
			dispatch(getProductReview(productId, currentPage, size));
		}
	}, [productId, currentPage, size, dispatch]);

	const handleImageError = (e) => {
		e.currentTarget.onerror = null;
		e.currentTarget.src = defaultImage;
	};

	if (!product) {
		return (
			<div style={{ padding: "40px", textAlign: "center" }}>
				<p>No product data found.</p>
				<button style={styles.backBtn} onClick={() => navigate(-1)}>
					<ArrowBackIcon sx={{ fontSize: 18, marginRight: "4px" }} /> Go Back
				</button>
			</div>
		);
	}

	// Combine mainImage + images (mainImage first, deduped) — mirrors mobile app behaviour.
	// This ensures the working primary photo is always shown even if some entries in `images`
	// reference stale/missing files on storage.
	const allImages = (() => {
		const main = product?.mainImage;
		const rest = product?.images || [];
		const combined = main ? [main, ...rest.filter((i) => i !== main)] : rest;
		return combined.map((img) => formatImageUrl(img)).filter(Boolean);
	})();
	const sellerName = product?.seller ? `${product.seller.firstName || ""} ${product.seller.lastName || ""}`.trim() || product.seller.businessName || product.seller.businessTag || "-" : "-";
	const categoryName = product?.category?.name || product?.category || "-";
	const subCategoryName = product?.subCategory?.name || product?.subCategory || "-";
	const stockLabel = product?.isOutOfStock ? "Out of Stock" : "In Stock";

	return (
		<div style={styles.page}>
			{/* Top Bar */}
			<div style={styles.topBar}>
				<button style={styles.backBtn} onClick={() => navigate(-1)}>
					<ArrowBackIcon sx={{ fontSize: 18, marginRight: "4px" }} /> Back
				</button>
				<h5 style={styles.pageTitle}>Product Detail</h5>
				<div style={{ width: "90px" }} />
			</div>

			{/* Status Banner */}
			<div
				style={{
					...styles.statusBanner,
					backgroundColor: product?.isOutOfStock ? "#ffebee" : "#e8f5e9",
					borderColor: product?.isOutOfStock ? "#ef5350" : "#66bb6a",
					color: product?.isOutOfStock ? "#c62828" : "#2e7d32",
				}}>
				<strong>Stock Status:</strong> {stockLabel} &nbsp;|&nbsp; <strong>Product Code:</strong> {product?.productCode || "-"}
			</div>

			<div className='row' style={{ margin: 0 }}>
				{/* Left — Images */}
				<div className='col-lg-5 col-md-12' style={{ padding: "0 12px" }}>
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-images me-2' style={{ color: "#e54a13" }}></i>
							Product Images
						</h6>
						{allImages.length > 0 ? (
							<>
								<div style={styles.mainImageWrap}>
									<img src={allImages[activeImgIndex]} alt='Product' style={styles.mainDisplayImg} onClick={() => setLightboxImg(allImages[activeImgIndex])} onError={handleImageError} />
								</div>
								{allImages.length > 1 && (
									<div style={styles.thumbnailRow}>
										{allImages.map((img, i) => (
											<img
												key={i}
												src={img}
												alt={`Thumb ${i + 1}`}
												style={{
													...styles.thumbnail,
													border: activeImgIndex === i ? "2px solid #e54a13" : "2px solid #e8e8e8",
												}}
												onClick={() => setActiveImgIndex(i)}
												onError={handleImageError}
											/>
										))}
									</div>
								)}
							</>
						) : (
							<div style={styles.noImage}>No Images Available</div>
						)}
					</div>
				</div>

				{/* Right — Details */}
				<div className='col-lg-7 col-md-12' style={{ padding: "0 12px" }}>
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-box-seam me-2' style={{ color: "#e54a13" }}></i>
							Product Information
						</h6>
						<div className='row'>
							<div className='col-md-6'>
								<InfoField label='Product Name' value={product?.productName} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Product Code' value={product?.productCode} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Sale Type' value={saleTypeLabel(product?.productSaleType)} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Stock' value={stockLabel} badgeTone={product?.isOutOfStock ? "danger" : "success"} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Quantity' value={product?.quantity != null ? product.quantity : "-"} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Sold' value={product?.sold != null ? product.sold : "-"} />
							</div>
							{product?.tags && (
								<div className='col-12'>
									<InfoField
										label='Tags'
										value={
											<div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
												{product.tags.split(",").map((tag, i) => (
													<span key={i} style={styles.tagBadge}>
														{tag.trim()}
													</span>
												))}
											</div>
										}
									/>
								</div>
							)}
							{product?.description && (
								<div className='col-12'>
									<InfoField label='Description' value={product?.description} />
								</div>
							)}
						</div>
					</div>

					{/* Pricing */}
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-currency-dollar me-2' style={{ color: "#e54a13" }}></i>
							Pricing
						</h6>
						<div className='row'>
							<div className='col-md-4'>
								<InfoField label='Price' value={`${symbol}${formatAmount(product?.price)}`} />
							</div>
							{product?.salePrice != null && product?.salePrice > 0 && (
								<div className='col-md-4'>
									<InfoField label='Sale Price' value={`${symbol}${formatAmount(product?.salePrice)}`} />
								</div>
							)}
							<div className='col-md-4'>
								<InfoField label='Shipping' value={`${symbol}${formatAmount(product?.shippingCharges)}`} />
							</div>
							<div className='col-md-4'>
								<InfoField label='Allow Offer' value={product?.allowOffer ? "Yes" : "No"} />
							</div>
							{product?.allowOffer && (
								<div className='col-md-4'>
									<InfoField label='Min. Offer Price' value={product?.minimumOfferPrice ? `${symbol}${formatAmount(product.minimumOfferPrice)}` : "-"} />
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Full Width Sections */}
			<div style={{ padding: "0 12px" }}>
				{/* Auction */}
				{product?.productSaleType === 2 && (
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-hammer me-2' style={{ color: "#e54a13" }}></i>
							Auction Details
						</h6>
						<div className='row'>
							<div className='col-md-3'>
								<InfoField label='Starting Price' value={product?.auctionStartingPrice ? `${symbol}${formatAmount(product.auctionStartingPrice)}` : "-"} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Reserve Price' value={product?.reservePrice ? `${symbol}${formatAmount(product.reservePrice)}` : "-"} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Duration (days)' value={product?.auctionDuration ?? "-"} />
							</div>
							<div className='col-md-3'>
								<InfoField label='Schedule Time' value={product?.scheduleTime ? new Date(product.scheduleTime).toLocaleString() : "-"} />
							</div>
						</div>
					</div>
				)}

				{/* Shipping */}
				{(product?.processingTime || product?.recipientAddress) && (
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-truck me-2' style={{ color: "#e54a13" }}></i>
							Shipping & Processing
						</h6>
						<div className='row'>
							<div className='col-md-6'>
								<InfoField label='Processing Time' value={product?.processingTime} />
							</div>
							<div className='col-md-6'>
								<InfoField label='Recipient Address' value={product?.recipientAddress} />
							</div>
						</div>
					</div>
				)}

				{/* Attributes */}
				{product?.attributes && product.attributes.length > 0 && (
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-tags me-2' style={{ color: "#e54a13" }}></i>
							Attributes
						</h6>
						<div className='row'>
							{product.attributes.map((attr, i) => {
								const titleMap = { colors: "Color", size: "Size", sleeves: "Sleeve" };
								return (
									<div className='col-md-4 col-sm-6 mb-3' key={attr._id || i}>
										<div style={styles.attrCard}>
											<div style={styles.attrName}>{titleMap[attr.name] || attr.name}</div>
											<div style={styles.attrValues}>
												{(attr.values || []).map((v, j) => (
													<span key={j} style={styles.attrBadge}>
														{v}
													</span>
												))}
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Seller & Category */}
				<div style={styles.section}>
					<h6 style={styles.sectionTitle}>
						<i className='bi bi-person-badge me-2' style={{ color: "#e54a13" }}></i>
						Seller & Category
					</h6>
					<div className='row'>
						<div className='col-md-4'>
							<InfoField label='Seller' value={sellerName} />
						</div>
						<div className='col-md-4'>
							<InfoField label='Category' value={categoryName} />
						</div>
						<div className='col-md-4'>
							<InfoField label='Sub Category' value={subCategoryName} />
						</div>
					</div>
				</div>

				{/* Timestamps */}
				{(product?.createdAt || product?.updatedAt || product?.date) && (
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-clock-history me-2' style={{ color: "#7a7a7a" }}></i>
							Timestamps
						</h6>
						<div className='row'>
							{product?.createdAt && (
								<div className='col-md-4'>
									<InfoField label='Created At' value={new Date(product.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
								</div>
							)}
							{product?.updatedAt && (
								<div className='col-md-4'>
									<InfoField label='Updated At' value={new Date(product.updatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
								</div>
							)}
							{product?.date && (
								<div className='col-md-4'>
									<InfoField label='Date' value={product.date} />
								</div>
							)}
						</div>
					</div>
				)}

				{/* Reviews */}
				<div style={styles.section}>
					<div className='d-flex align-items-center justify-content-between mb-3'>
						<h6 style={{ ...styles.sectionTitle, marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
							<i className='bi bi-chat-square-text me-2' style={{ color: "#e54a13" }}></i>
							Customer Reviews
						</h6>
						<span style={styles.reviewBadge}>{review?.length || 0} Reviews</span>
					</div>

					{review?.length > 0 ? (
						<div className='row g-3'>
							{review.map((r, i) => (
								<div className='col-12' key={i}>
									<div style={styles.reviewCard}>
										<div className='d-flex gap-3'>
											<img src={formatImageUrl(r.userImage)} alt='user' onError={handleImageError} style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover" }} />
											<div className='flex-grow-1'>
												<div className='d-flex justify-content-between align-items-start mb-2'>
													<div>
														<div style={{ fontWeight: 600, color: "#333" }}>
															{r.firstName} {r.lastName}
														</div>
														<small style={{ color: "#999" }}>{r.time}</small>
													</div>
													<Rating initialValue={r.rating} readonly allowFraction size={16} />
												</div>
												<p style={{ margin: 0, fontSize: "13px", color: "#666" }}>{r.review}</p>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
							<i className='bi bi-chat-square-text' style={{ fontSize: "2.5rem" }}></i>
							<p style={{ marginTop: "8px", marginBottom: 0 }}>No reviews yet</p>
						</div>
					)}
				</div>
			</div>

			{/* Lightbox */}
			{lightboxImg && (
				<>
					<div style={styles.lightboxOverlay} onClick={() => setLightboxImg(null)} />
					<div style={styles.lightboxContainer} onClick={() => setLightboxImg(null)}>
						<img src={lightboxImg} alt='Full view' style={styles.lightboxImg} />
						<button style={styles.lightboxClose} onClick={() => setLightboxImg(null)}>
							<i className='bi bi-x-lg'></i>
						</button>
					</div>
				</>
			)}
		</div>
	);
};

const InfoField = ({ label, value, badgeTone }) => (
	<div style={{ marginBottom: "12px" }}>
		<label style={styles.fieldLabel}>{label}</label>
		{badgeTone ? (
			<span
				style={{
					display: "inline-block",
					padding: "2px 10px",
					borderRadius: "4px",
					fontSize: "13px",
					fontWeight: 600,
					backgroundColor: badgeTone === "success" ? "rgba(40,167,69,0.12)" : badgeTone === "danger" ? "rgba(220,53,69,0.12)" : "rgba(255,193,7,0.12)",
					color: badgeTone === "success" ? "#1e7e34" : badgeTone === "danger" ? "#c82333" : "#d4a017",
				}}>
				{value || "-"}
			</span>
		) : (
			<div style={styles.fieldValue}>{value || value === 0 ? value : "-"}</div>
		)}
	</div>
);

const styles = {
	page: {
		padding: "20px",
		backgroundColor: "#f4f4f8",
		minHeight: "calc(100vh - 70px)",
	},
	topBar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: "16px",
		flexWrap: "wrap",
		gap: "10px",
	},
	backBtn: {
		display: "flex",
		alignItems: "center",
		background: "#fff",
		border: "1px solid #ddd",
		borderRadius: "8px",
		padding: "8px 16px",
		cursor: "pointer",
		fontSize: "14px",
		fontWeight: 500,
		color: "#333",
	},
	pageTitle: {
		margin: 0,
		color: "#333",
		fontSize: "20px",
		fontWeight: 600,
		flex: 1,
		textAlign: "center",
	},
	statusBanner: {
		padding: "12px 20px",
		borderRadius: "8px",
		border: "1px solid",
		fontSize: "14px",
		fontWeight: 500,
		marginBottom: "16px",
	},
	section: {
		marginBottom: "20px",
		padding: "20px",
		backgroundColor: "#fff",
		borderRadius: "10px",
		border: "1px solid #e8e8e8",
		boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
	},
	sectionTitle: {
		color: "#333",
		fontWeight: 600,
		marginBottom: "16px",
		paddingBottom: "10px",
		borderBottom: "1px solid #f0f0f0",
		fontSize: "15px",
	},
	fieldLabel: {
		fontSize: "11px",
		fontWeight: 600,
		color: "#999",
		textTransform: "uppercase",
		letterSpacing: "0.5px",
		display: "block",
		marginBottom: "4px",
	},
	fieldValue: {
		color: "#333",
		fontSize: "14px",
		lineHeight: "1.5",
		wordBreak: "break-word",
	},
	mainImageWrap: {
		textAlign: "center",
		marginBottom: "12px",
	},
	mainDisplayImg: {
		maxWidth: "100%",
		maxHeight: "400px",
		objectFit: "contain",
		borderRadius: "10px",
		cursor: "pointer",
		border: "2px solid #e8e8e8",
	},
	thumbnailRow: {
		display: "flex",
		gap: "8px",
		justifyContent: "center",
		flexWrap: "wrap",
	},
	thumbnail: {
		width: "65px",
		height: "65px",
		objectFit: "cover",
		borderRadius: "8px",
		cursor: "pointer",
		transition: "border-color 0.2s",
	},
	noImage: {
		textAlign: "center",
		padding: "60px",
		color: "#999",
		fontSize: "14px",
	},
	attrCard: {
		backgroundColor: "#f9f9fb",
		borderRadius: "8px",
		padding: "14px",
		border: "1px solid #e8e8e8",
	},
	attrName: {
		color: "#333",
		fontWeight: 600,
		fontSize: "13px",
		marginBottom: "6px",
	},
	attrValues: {
		display: "flex",
		flexWrap: "wrap",
		gap: "4px",
	},
	attrBadge: {
		display: "inline-block",
		padding: "2px 10px",
		borderRadius: "4px",
		backgroundColor: "rgba(229, 74, 19, 0.08)",
		color: "#e54a13",
		fontSize: "12px",
		fontWeight: 500,
	},
	tagBadge: {
		display: "inline-block",
		padding: "4px 12px",
		borderRadius: "20px",
		backgroundColor: "#f0f0f0",
		color: "#333",
		fontSize: "12px",
		fontWeight: 500,
	},
	reviewBadge: {
		fontSize: "13px",
		fontWeight: 600,
		backgroundColor: "#e54a13",
		color: "#fff",
		padding: "4px 12px",
		borderRadius: "6px",
	},
	reviewCard: {
		border: "1px solid #eee",
		borderRadius: "8px",
		padding: "14px",
		backgroundColor: "#fafafa",
	},
	lightboxOverlay: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0,0,0,0.85)",
		zIndex: 9998,
	},
	lightboxContainer: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		zIndex: 9999,
		cursor: "zoom-out",
	},
	lightboxImg: {
		maxWidth: "90vw",
		maxHeight: "90vh",
		objectFit: "contain",
		borderRadius: "8px",
	},
	lightboxClose: {
		position: "absolute",
		top: "20px",
		right: "20px",
		width: "40px",
		height: "40px",
		borderRadius: "50%",
		border: "none",
		backgroundColor: "#fff",
		color: "#333",
		fontSize: "18px",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
};

export default connect(null, {
	getProductDetail,
	outOfStock,
	getProductReview,
})(ProductDetail);
